import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, finalize, forkJoin } from 'rxjs';
import {
  CatalogCategory,
  CatalogDeleteImpact,
  CatalogCompatibility,
  CatalogComponent,
  CatalogComponentType,
  CatalogProduct,
  CatalogProductStructure
} from '../configurator/configurator.models';
import { ConfiguratorApiService } from '../core/api/configurator-api.service';
import { I18nService } from '../core/i18n/i18n.service';
import { TranslatePipe } from '../core/i18n/translate.pipe';

type TableKey = 'categories' | 'products' | 'componentTypes' | 'components' | 'productStructures' | 'compatibilities';
type ContentCardKey = 'categories' | 'products' | 'componentTypes' | 'components' | 'productStructures' | 'compatibilities';

interface TableState {
  query: string;
  facet: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

@Component({
  selector: 'app-product-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './product-admin-page.component.html',
  styleUrl: './shared-page.css'
})
export class ProductAdminPageComponent implements OnInit {
  private readonly api = inject(ConfiguratorApiService);
  private readonly i18n = inject(I18nService);
  activeSection: 'PRODUCTS' | 'COMPONENTS' | 'STRUCTURES' | 'COMPATIBILITIES' = 'PRODUCTS';

  categories: CatalogCategory[] = [];
  products: CatalogProduct[] = [];
  componentTypes: CatalogComponentType[] = [];
  components: CatalogComponent[] = [];
  productStructures: CatalogProductStructure[] = [];
  compatibilities: CatalogCompatibility[] = [];

  categoryForm: { id: string | null; code: string; name: string } = { id: null, code: '', name: '' };
  productForm: { id: string | null; code: string; name: string; categoryId: string } = {
    id: null,
    code: '',
    name: '',
    categoryId: ''
  };
  componentTypeForm: { id: string | null; code: string; name: string } = { id: null, code: '', name: '' };
  componentForm: { id: string | null; componentTypeId: string; code: string; name: string; description: string } = {
    id: null,
    componentTypeId: '',
    code: '',
    name: '',
    description: ''
  };
  structureForm: { id: string | null; productId: string; componentTypeId: string; quantity: number; orderNo: number } = {
    id: null,
    productId: '',
    componentTypeId: '',
    quantity: 1,
    orderNo: 1
  };
  compatibilityForm: { id: string | null; productId: string; componentId: string; required: boolean; defaultSelected: boolean } = {
    id: null,
    productId: '',
    componentId: '',
    required: false,
    defaultSelected: false
  };

  busy = false;
  error = '';
  info = '';
  deleteDialogOpen = false;
  deleteDialogLoading = false;
  deleteDialogTitle = '';
  deleteDialogDescription = '';
  deleteDialogImpact: CatalogDeleteImpact | null = null;
  private pendingDeleteAction: (() => Observable<unknown>) | null = null;
  private pendingDeleteSuccessMessage = '';
  private pendingDeleteResetAction: () => void = () => {};
  collapsedCards: Record<ContentCardKey, boolean> = {
    categories: false,
    products: false,
    componentTypes: false,
    components: false,
    productStructures: false,
    compatibilities: false
  };
  tableState: Record<TableKey, TableState> = {
    categories: { query: '', facet: '', page: 1, pageSize: 10, sortBy: 'code', sortDirection: 'asc' },
    products: { query: '', facet: '', page: 1, pageSize: 10, sortBy: 'code', sortDirection: 'asc' },
    componentTypes: { query: '', facet: '', page: 1, pageSize: 10, sortBy: 'code', sortDirection: 'asc' },
    components: { query: '', facet: '', page: 1, pageSize: 10, sortBy: 'code', sortDirection: 'asc' },
    productStructures: { query: '', facet: '', page: 1, pageSize: 10, sortBy: 'productName', sortDirection: 'asc' },
    compatibilities: { query: '', facet: '', page: 1, pageSize: 10, sortBy: 'productName', sortDirection: 'asc' }
  };
  expandedRows: Partial<Record<TableKey, string | null>> = {
    categories: null,
    products: null,
    componentTypes: null,
    components: null,
    productStructures: null,
    compatibilities: null
  };

  ngOnInit(): void {
    this.reloadData();
  }

  reloadData(): void {
    this.error = '';
    this.info = '';
    this.busy = true;

    forkJoin({
      categories: this.api.listCategories(),
      products: this.api.listCatalogProducts(),
      componentTypes: this.api.listComponentTypes(),
      components: this.api.listComponents(),
      productStructures: this.api.listProductStructures(),
      compatibilities: this.api.listCompatibilities()
    }).pipe(finalize(() => this.busy = false)).subscribe({
      next: (data) => {
        this.categories = data.categories;
        this.products = data.products;
        this.componentTypes = data.componentTypes;
        this.components = data.components;
        this.productStructures = data.productStructures;
        this.compatibilities = data.compatibilities;
        this.expandedRows = {
          categories: null,
          products: null,
          componentTypes: null,
          components: null,
          productStructures: null,
          compatibilities: null
        };
      },
      error: (err) => this.error = this.extractError(err)
    });
  }

  saveCategory(): void {
    if (!this.categoryForm.code.trim() || !this.categoryForm.name.trim()) {
      this.error = this.i18n.t('product.error.categoryRequired');
      return;
    }

    this.busy = true;
    this.error = '';
    this.info = '';

    const payload = { code: this.categoryForm.code.trim(), name: this.categoryForm.name.trim() };
    if (this.categoryForm.id) {
      this.runSave(this.api.updateCategory(this.categoryForm.id, payload), this.i18n.t('product.info.categoryUpdated'), () => this.resetCategoryForm());
    } else {
      this.runSave(this.api.createCategory(payload), this.i18n.t('product.info.categoryCreated'), () => this.resetCategoryForm());
    }
  }

  editCategory(item: CatalogCategory): void {
    this.categoryForm = { id: item.id, code: item.code, name: item.name };
  }

  resetCategoryForm(): void {
    this.categoryForm = { id: null, code: '', name: '' };
  }

  deleteCategory(item: CatalogCategory): void {
    this.openDeleteDialog(
      'category',
      item.id,
      this.i18n.t('product.delete.categoryTitle', { name: item.name }),
      () => this.api.deleteCategory(item.id),
      this.i18n.t('product.info.categoryDeleted'),
      () => {
      if (this.categoryForm.id === item.id) {
        this.resetCategoryForm();
      }
      }
    );
  }

  saveProduct(): void {
    if (!this.productForm.code.trim() || !this.productForm.name.trim()) {
      this.error = this.i18n.t('product.error.productRequired');
      return;
    }

    this.busy = true;
    this.error = '';
    this.info = '';

    const payload = {
      code: this.productForm.code.trim(),
      name: this.productForm.name.trim(),
      categoryId: this.normalizeNullableId(this.productForm.categoryId)
    };
    if (this.productForm.id) {
      this.runSave(this.api.updateCatalogProduct(this.productForm.id, payload), this.i18n.t('product.info.productUpdated'), () => this.resetProductForm());
    } else {
      this.runSave(this.api.createCatalogProduct(payload), this.i18n.t('product.info.productCreated'), () => this.resetProductForm());
    }
  }

  editProduct(item: CatalogProduct): void {
    this.productForm = {
      id: item.id,
      code: item.code,
      name: item.name,
      categoryId: item.categoryId ?? ''
    };
  }

  resetProductForm(): void {
    this.productForm = { id: null, code: '', name: '', categoryId: '' };
  }

  deleteProduct(item: CatalogProduct): void {
    this.openDeleteDialog(
      'product',
      item.id,
      this.i18n.t('product.delete.productTitle', { name: item.name }),
      () => this.api.deleteCatalogProduct(item.id),
      this.i18n.t('product.info.productDeleted'),
      () => {
      if (this.productForm.id === item.id) {
        this.resetProductForm();
      }
      }
    );
  }

  saveComponentType(): void {
    if (!this.componentTypeForm.code.trim() || !this.componentTypeForm.name.trim()) {
      this.error = this.i18n.t('product.error.componentTypeRequired');
      return;
    }

    this.busy = true;
    this.error = '';
    this.info = '';

    const payload = { code: this.componentTypeForm.code.trim(), name: this.componentTypeForm.name.trim() };
    if (this.componentTypeForm.id) {
      this.runSave(this.api.updateComponentType(this.componentTypeForm.id, payload), this.i18n.t('product.info.componentTypeUpdated'), () => this.resetComponentTypeForm());
    } else {
      this.runSave(this.api.createComponentType(payload), this.i18n.t('product.info.componentTypeCreated'), () => this.resetComponentTypeForm());
    }
  }

  editComponentType(item: CatalogComponentType): void {
    this.componentTypeForm = { id: item.id, code: item.code, name: item.name };
  }

  resetComponentTypeForm(): void {
    this.componentTypeForm = { id: null, code: '', name: '' };
  }

  deleteComponentType(item: CatalogComponentType): void {
    this.openDeleteDialog(
      'component-type',
      item.id,
      this.i18n.t('product.delete.componentTypeTitle', { name: item.name }),
      () => this.api.deleteComponentType(item.id),
      this.i18n.t('product.info.componentTypeDeleted'),
      () => {
      if (this.componentTypeForm.id === item.id) {
        this.resetComponentTypeForm();
      }
      }
    );
  }

  saveComponent(): void {
    if (!this.componentForm.componentTypeId || !this.componentForm.code.trim() || !this.componentForm.name.trim()) {
      this.error = this.i18n.t('product.error.componentRequired');
      return;
    }

    this.busy = true;
    this.error = '';
    this.info = '';

    const payload = {
      componentTypeId: this.componentForm.componentTypeId,
      code: this.componentForm.code.trim(),
      name: this.componentForm.name.trim(),
      description: this.normalizeNullableText(this.componentForm.description)
    };
    if (this.componentForm.id) {
      this.runSave(this.api.updateComponent(this.componentForm.id, payload), this.i18n.t('product.info.componentUpdated'), () => this.resetComponentForm());
    } else {
      this.runSave(this.api.createComponent(payload), this.i18n.t('product.info.componentCreated'), () => this.resetComponentForm());
    }
  }

  editComponent(item: CatalogComponent): void {
    this.componentForm = {
      id: item.id,
      componentTypeId: item.componentTypeId,
      code: item.code,
      name: item.name,
      description: item.description ?? ''
    };
  }

  resetComponentForm(): void {
    this.componentForm = { id: null, componentTypeId: '', code: '', name: '', description: '' };
  }

  deleteComponent(item: CatalogComponent): void {
    this.openDeleteDialog(
      'component',
      item.id,
      this.i18n.t('product.delete.componentTitle', { name: item.name }),
      () => this.api.deleteComponent(item.id),
      this.i18n.t('product.info.componentDeleted'),
      () => {
      if (this.componentForm.id === item.id) {
        this.resetComponentForm();
      }
      }
    );
  }

  saveProductStructure(): void {
    if (!this.structureForm.productId
      || !this.structureForm.componentTypeId
      || this.structureForm.quantity < 1
      || this.structureForm.orderNo < 1) {
      this.error = this.i18n.t('product.error.structureRequired');
      return;
    }

    this.busy = true;
    this.error = '';
    this.info = '';

    const payload = {
      productId: this.structureForm.productId,
      componentTypeId: this.structureForm.componentTypeId,
      quantity: this.structureForm.quantity,
      orderNo: this.structureForm.orderNo
    };
    if (this.structureForm.id) {
      this.runSave(this.api.updateProductStructure(this.structureForm.id, payload), this.i18n.t('product.info.structureUpdated'), () => this.resetProductStructureForm());
    } else {
      this.runSave(this.api.createProductStructure(payload), this.i18n.t('product.info.structureCreated'), () => this.resetProductStructureForm());
    }
  }

  editProductStructure(item: CatalogProductStructure): void {
    this.structureForm = {
      id: item.id,
      productId: item.productId,
      componentTypeId: item.componentTypeId,
      quantity: item.quantity,
      orderNo: item.orderNo
    };
  }

  resetProductStructureForm(): void {
    this.structureForm = { id: null, productId: '', componentTypeId: '', quantity: 1, orderNo: 1 };
  }

  deleteProductStructure(item: CatalogProductStructure): void {
    this.openDeleteDialog(
      'product-structure',
      item.id,
      this.i18n.t('product.delete.structureTitle'),
      () => this.api.deleteProductStructure(item.id),
      this.i18n.t('product.info.structureDeleted'),
      () => {
      if (this.structureForm.id === item.id) {
        this.resetProductStructureForm();
      }
      }
    );
  }

  saveCompatibility(): void {
    if (!this.compatibilityForm.productId || !this.compatibilityForm.componentId) {
      this.error = this.i18n.t('product.error.compatibilityRequired');
      return;
    }

    this.busy = true;
    this.error = '';
    this.info = '';

    const payload = {
      productId: this.compatibilityForm.productId,
      componentId: this.compatibilityForm.componentId,
      required: this.compatibilityForm.required,
      defaultSelected: this.compatibilityForm.defaultSelected
    };
    if (this.compatibilityForm.id) {
      this.runSave(this.api.updateCompatibility(this.compatibilityForm.id, payload), this.i18n.t('product.info.compatibilityUpdated'), () => this.resetCompatibilityForm());
    } else {
      this.runSave(this.api.createCompatibility(payload), this.i18n.t('product.info.compatibilityCreated'), () => this.resetCompatibilityForm());
    }
  }

  editCompatibility(item: CatalogCompatibility): void {
    this.compatibilityForm = {
      id: item.id,
      productId: item.productId,
      componentId: item.componentId,
      required: item.required,
      defaultSelected: item.defaultSelected
    };
  }

  resetCompatibilityForm(): void {
    this.compatibilityForm = { id: null, productId: '', componentId: '', required: false, defaultSelected: false };
  }

  deleteCompatibility(item: CatalogCompatibility): void {
    this.openDeleteDialog(
      'compatibility',
      item.id,
      this.i18n.t('product.delete.compatibilityTitle'),
      () => this.api.deleteCompatibility(item.id),
      this.i18n.t('product.info.compatibilityDeleted'),
      () => {
      if (this.compatibilityForm.id === item.id) {
        this.resetCompatibilityForm();
      }
      }
    );
  }

  closeDeleteDialog(): void {
    this.deleteDialogOpen = false;
    this.deleteDialogImpact = null;
    this.pendingDeleteAction = null;
    this.pendingDeleteSuccessMessage = '';
    this.pendingDeleteResetAction = () => {};
  }

  confirmDeleteFromDialog(): void {
    if (!this.pendingDeleteAction || this.deleteDialogLoading) {
      return;
    }
    this.busy = true;
    this.deleteDialogLoading = true;
    this.error = '';
    this.info = '';
    this.pendingDeleteAction()
      .pipe(finalize(() => {
        this.busy = false;
        this.deleteDialogLoading = false;
      }))
      .subscribe({
        next: () => {
          this.info = this.pendingDeleteSuccessMessage;
          this.pendingDeleteResetAction();
          this.closeDeleteDialog();
          this.reloadData();
        },
        error: (err: unknown) => {
          this.error = this.extractError(err);
        }
      });
  }

  formatDeleteDependent(item: { entityType: string; label: string }): string {
    return `${this.humanizeEntityType(item.entityType)}: ${item.label}`;
  }

  onFilterChanged(key: TableKey): void {
    this.tableState[key].page = 1;
  }

  onFacetChanged(key: TableKey): void {
    this.tableState[key].page = 1;
  }

  toggleCardCollapse(key: ContentCardKey): void {
    this.collapsedCards[key] = !this.collapsedCards[key];
  }

  isCardCollapsed(key: ContentCardKey): boolean {
    return this.collapsedCards[key];
  }

  setPageSize(key: TableKey, pageSize: string): void {
    const parsed = Number(pageSize);
    this.tableState[key].pageSize = Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
    this.tableState[key].page = 1;
  }

  toggleSort(key: TableKey, field: string): void {
    const state = this.tableState[key];
    if (state.sortBy === field) {
      state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      state.sortBy = field;
      state.sortDirection = 'asc';
    }
    state.page = 1;
  }

  sortIndicator(key: TableKey, field: string): string {
    const state = this.tableState[key];
    if (state.sortBy !== field) {
      return '';
    }
    return state.sortDirection === 'asc' ? '▲' : '▼';
  }

  previousPage(key: TableKey): void {
    this.tableState[key].page = Math.max(1, this.tableState[key].page - 1);
  }

  nextPage(key: TableKey): void {
    const total = this.totalPages(key);
    this.tableState[key].page = Math.min(total, this.tableState[key].page + 1);
  }

  pagedItems(key: TableKey): any[] {
    const state = this.tableState[key];
    const filtered = this.filteredItems(key);
    const total = this.totalPages(key);
    if (state.page > total) {
      state.page = total;
    }
    const start = (state.page - 1) * state.pageSize;
    return filtered.slice(start, start + state.pageSize);
  }

  totalPages(key: TableKey): number {
    const totalItems = this.filteredItems(key).length;
    return Math.max(1, Math.ceil(totalItems / this.tableState[key].pageSize));
  }

  visibleCount(key: TableKey): number {
    return this.filteredItems(key).length;
  }

  toggleRowExpansion(key: TableKey, itemId: string): void {
    this.expandedRows[key] = this.expandedRows[key] === itemId ? null : itemId;
  }

  isRowExpanded(key: TableKey, itemId: string): boolean {
    return this.expandedRows[key] === itemId;
  }

  formatAuditTimestamp(value: string): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.i18n.language === 'de' ? 'de-DE' : 'en-US';
    return date.toLocaleString(locale);
  }

  private normalizeNullableId(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private normalizeNullableText(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private filteredItems(key: TableKey): unknown[] {
    const state = this.tableState[key];
    const query = state.query.trim().toLowerCase();
    const items = this.itemsForKey(key);
    const filtered = items.filter((item) => {
      const matchesQuery = !query || this.searchTextForItem(key, item).includes(query);
      const matchesFacet = this.matchesFacetFilter(key, item, state.facet);
      return matchesQuery && matchesFacet;
    });
    return this.sortItems(key, filtered);
  }

  private matchesFacetFilter(key: TableKey, item: unknown, facet: string): boolean {
    if (!facet) {
      return true;
    }

    switch (key) {
      case 'products': {
        const value = item as CatalogProduct;
        return (value.categoryId ?? '') === facet;
      }
      case 'components': {
        const value = item as CatalogComponent;
        return value.componentTypeId === facet;
      }
      case 'productStructures': {
        const value = item as CatalogProductStructure;
        return value.productId === facet;
      }
      default:
        return true;
    }
  }

  private sortItems(key: TableKey, items: unknown[]): unknown[] {
    const state = this.tableState[key];
    const direction = state.sortDirection === 'asc' ? 1 : -1;

    return [...items].sort((left, right) => {
      const leftValue = this.sortValueForItem(key, left, state.sortBy);
      const rightValue = this.sortValueForItem(key, right, state.sortBy);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * direction;
      }

      return String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: 'base', numeric: true }) * direction;
    });
  }

  private sortValueForItem(key: TableKey, item: unknown, field: string): string | number {
    switch (key) {
      case 'categories': {
        const value = item as CatalogCategory;
        if (field === 'name') {
          return value.name ?? '';
        }
        return value.code ?? '';
      }
      case 'products': {
        const value = item as CatalogProduct;
        switch (field) {
          case 'name':
            return value.name ?? '';
          case 'categoryName':
            return value.categoryName ?? '';
          default:
            return value.code ?? '';
        }
      }
      case 'componentTypes': {
        const value = item as CatalogComponentType;
        if (field === 'name') {
          return value.name ?? '';
        }
        return value.code ?? '';
      }
      case 'components': {
        const value = item as CatalogComponent;
        switch (field) {
          case 'name':
            return value.name ?? '';
          case 'componentTypeName':
            return value.componentTypeName ?? '';
          default:
            return value.code ?? '';
        }
      }
      case 'productStructures': {
        const value = item as CatalogProductStructure;
        switch (field) {
          case 'orderNo':
            return value.orderNo;
          case 'componentTypeName':
            return value.componentTypeName ?? '';
          case 'quantity':
            return value.quantity;
          default:
            return value.productName ?? '';
        }
      }
      case 'compatibilities': {
        const value = item as CatalogCompatibility;
        switch (field) {
          case 'componentName':
            return value.componentName ?? '';
          case 'required':
            return value.required ? 1 : 0;
          case 'defaultSelected':
            return value.defaultSelected ? 1 : 0;
          default:
            return value.productName ?? '';
        }
      }
    }
  }

  private itemsForKey(key: TableKey): unknown[] {
    switch (key) {
      case 'categories':
        return this.categories;
      case 'products':
        return this.products;
      case 'componentTypes':
        return this.componentTypes;
      case 'components':
        return this.components;
      case 'productStructures':
        return this.productStructures;
      case 'compatibilities':
        return this.compatibilities;
    }
  }

  private searchTextForItem(key: TableKey, item: unknown): string {
    switch (key) {
      case 'categories': {
        const value = item as CatalogCategory;
        return `${value.code} ${value.name}`.toLowerCase();
      }
      case 'products': {
        const value = item as CatalogProduct;
        return `${value.code} ${value.name} ${value.categoryName ?? ''}`.toLowerCase();
      }
      case 'componentTypes': {
        const value = item as CatalogComponentType;
        return `${value.code} ${value.name}`.toLowerCase();
      }
      case 'components': {
        const value = item as CatalogComponent;
        return `${value.code} ${value.name} ${value.description ?? ''} ${value.componentTypeName ?? ''}`.toLowerCase();
      }
      case 'productStructures': {
        const value = item as CatalogProductStructure;
        return `${value.productName ?? ''} ${value.componentTypeName ?? ''} ${value.quantity} ${value.orderNo}`.toLowerCase();
      }
      case 'compatibilities': {
        const value = item as CatalogCompatibility;
        return `${value.productName ?? ''} ${value.componentName ?? ''} ${value.required ? 'required' : 'optional'} ${value.defaultSelected ? 'default' : ''}`.toLowerCase();
      }
    }
  }

  private runSave(request: Observable<unknown>, message: string, reset: () => void): void {
    request.pipe(finalize(() => this.busy = false)).subscribe({
      next: () => {
        this.info = message;
        reset();
        this.reloadData();
      },
      error: (err: unknown) => this.error = this.extractError(err)
    });
  }

  private openDeleteDialog(
    entityType: string,
    entityId: string,
    title: string,
    requestFactory: () => Observable<unknown>,
    successMessage: string,
    resetAction: () => void
  ): void {
    this.error = '';
    this.info = '';
    this.deleteDialogOpen = true;
    this.deleteDialogLoading = true;
    this.deleteDialogTitle = title;
    this.deleteDialogDescription = this.i18n.t('common.actionCannotUndo');
    this.deleteDialogImpact = null;
    this.pendingDeleteAction = requestFactory;
    this.pendingDeleteSuccessMessage = successMessage;
    this.pendingDeleteResetAction = resetAction;

    this.api.getCatalogDeleteImpact(entityType, entityId)
      .pipe(finalize(() => this.deleteDialogLoading = false))
      .subscribe({
        next: (impact) => {
          this.deleteDialogImpact = impact;
          this.deleteDialogDescription = impact.dependents.length
            ? this.i18n.t('product.delete.dependentsCount', { count: impact.dependents.length })
            : this.i18n.t('product.delete.noDependents');
        },
        error: (err: unknown) => {
          this.error = this.extractError(err);
        }
      });
  }

  private humanizeEntityType(value: string): string {
    return this.i18n.t(`entity.${value}`);
  }

  private extractError(err: unknown): string {
    if (typeof err === 'object' && err !== null && 'error' in err) {
      const payload = (err as { error?: { error?: string } }).error;
      if (payload?.error) {
        return payload.error;
      }
    }
    return this.i18n.t('common.requestFailedJwt');
  }
}
